import * as ex from 'excalibur'
import { leftRoadEdge, rightRoadEdge } from "./constants";
import { RacerTrait } from "./RacerTrait";
import { getTrait, wrap } from './util';
import { TagTrait } from './TagTrait';
import { TrailerTrait } from './TrailerTrait';
import { AutoTurretTrait } from './AutoTurretTrait';

type TextureLibrary = {[i:string]:ex.Texture};
type SoundLibrary = {[i:string]:ex.Sound};

export class TitleScene extends ex.Scene {
    readonly textures: TextureLibrary;
    readonly sounds: SoundLibrary;
    mouseHandler = (evt?:ex.GameEvent<any>) => {
        let e = evt! as ex.Input.PointerEvent;
        if (this.playButton!.contains(e.screenPos.x, e.screenPos.y)) {
            this.engine.goToScene("game");
        }
    };
    constructor(textures: TextureLibrary, sounds:SoundLibrary) {
        super();
        this.textures = textures;
        this.sounds = sounds;
    }

    title : ex.Actor | undefined;
    playButton : ex.Actor | undefined;
    onInitialize(engine: ex.Engine) {
        this.title = new ex.Actor(this.engine.halfDrawWidth, this.engine.halfDrawHeight, 600, 300);
        this.title.addDrawing(this.textures["title"]);
        this.addUIActor(this.title);

        this.playButton = new ex.Actor(this.engine.halfDrawWidth, this.engine.halfDrawHeight + 190, 128, 64);
        this.playButton.addDrawing(this.textures["playButton"]);
        this.addUIActor(this.playButton);
        super.onInitialize(engine);
    }
    onActivate(oldScene: ex.Scene, newScene: ex.Scene) {
        super.onActivate(oldScene, newScene);
        setTimeout(() => { this.sounds["title"].play(0.8) }, 200);
        this.engine.input.pointers.primary.on("down", this.mouseHandler);
    }

    onDeactivate(oldScene: ex.Scene, newScene: ex.Scene) {
        super.onDeactivate(oldScene, newScene);
        this.engine.input.pointers.primary.off("down", this.mouseHandler);
    }
    update(engine: ex.Engine, delta: number): void {
        super.update(engine, delta);
    }
}

const turretCost = 50;

const stripeLength = 50;
const stripeInterval = 100;

function findMin<T>(arr: T[], f: (item:T) => number) {
    let winner = Infinity;
    for (let t of arr) {
        let i = f(t);
        if (i < winner) {
            winner = i;
        }
    }
    return winner;
}

function findMax<T>(arr: T[], f: (item:T) => number) {
    let winner = -Infinity;
    for (let t of arr) {
        let i = f(t);
        if (i > winner) {
            winner = i;
        }
    }
    return winner;
}

export class GameScene extends ex.Scene {
    readonly lib : TextureLibrary;
    readonly sounds: SoundLibrary;
    tower : ex.Actor | undefined;

    leftSide : ex.Actor | undefined;
    rightSide : ex.Actor | undefined;

    throttleOn = false;
    steerLeft = false;
    steerRight = false;

    mines: ex.Actor[] = [];

    chasers: ex.Actor[] = [];

    gameOver = false;

    gameOverText: ex.Actor | undefined;

    lastMine = 1000;

    lastChaserTime = 100;

    score = 0;
    lastScoreIncreaseDistance = 0;

    scoreLabel: ex.Label | undefined;

    gold = 0;
    goldLabel: ex.Label | undefined;

    buyButton: ex.Actor | undefined;

    trailers: ex.Actor[] = [];

    gameTime = 0;

    chaserRateMultiplier = 1;

    continueButton: ex.Actor | undefined;

    stripes: ex.Actor[] = [];
    topStripePos = 500;
    bottomStripePos = 500;

    mouseHandler = (evt?:ex.GameEvent<any>) => {
        let e = evt as ex.Input.PointerEvent;
        if (this.buyButton!.contains(e.screenPos.x, e.screenPos.y)) {
            this.buyTurret();
        }
        else if (this.gameOver && this.continueButton!.contains(e.screenPos.x, e.screenPos.y)) {
            this.engine.goToScene("title");
        }
    };

    constructor(lib: TextureLibrary, sounds : SoundLibrary) {
        super();
        this.lib = lib;
        this.sounds = sounds;
    }

    onActivate(oldScene: ex.Scene, newScene: ex.Scene) {
        this.reset();
        this.setup();
        this.engine.input.pointers.primary.on("down", this.mouseHandler);
        super.onActivate(oldScene, newScene);
    }

    onDeactivate(oldScene: ex.Scene, newScene: ex.Scene) {
        this.reset();
        this.engine.input.pointers.primary.off("down", this.mouseHandler);
        super.onDeactivate(oldScene, newScene);
    }

    reset() {
        for (let actor of this.actors.slice()) {
            this.remove(actor);
        }
        for (let uiActor of this.uiActors.slice()) {
            this.removeUIActor(uiActor);
        }

        this.tower = undefined;
        this.throttleOn = false;
        this.steerLeft = false;
        this.steerRight = false;
        this.mines = [];
        this.chasers = [];
        this.gameOver = false;
        this.gameOverText = undefined;
        this.lastMine = 1000;
        this.lastChaserTime = 100;
        this.score = 0;
        this.lastScoreIncreaseDistance = 0;
        this.scoreLabel = undefined;
        this.gold = 0;
        this.goldLabel = undefined;
        this.buyButton = undefined;
        this.trailers = [];
        this.gameTime = 0;
        this.chaserRateMultiplier = 1;
        this.continueButton = undefined;
        this.stripes = [];
        this.topStripePos = 500;
        this.bottomStripePos = 500;
    }

    setup() {
        let grassColor = ex.Color.fromRGB(102, 204, 0);
        this.leftSide = new ex.Actor(leftRoadEdge, 0, 100000, 100000);
        this.leftSide.anchor.setTo(1, 0.5);
        this.leftSide.color = grassColor;

        this.add(this.leftSide);

        this.rightSide = new ex.Actor(rightRoadEdge, 0, 100000, 100000);
        this.rightSide.anchor.setTo(0, 0.5);
        this.rightSide.color = grassColor;

        this.add(this.rightSide);

        this.tower = this.createTower();
        this.tower.on("precollision", evt => { this.onActorCollision(evt!, this.tower!)});
        this.add(this.tower);
        this.trailers.push(this.tower);

        this.camera.strategy.lockToActor(this.tower);

        this.gameOverText = new ex.Actor(this.engine.halfDrawWidth, this.engine.halfDrawHeight);
        this.gameOverText.addDrawing(this.lib["gameOver"]);

        this.continueButton = new ex.Actor(this.engine.halfDrawWidth, this.engine.halfDrawHeight + 150, 118, 54);
        this.continueButton.addDrawing(this.lib["continueButton"]);

        this.scoreLabel = new ex.Label("0", this.engine.drawWidth - 20, this.engine.drawHeight - 20);
        this.scoreLabel.scale.scaleEqual(2);
        this.scoreLabel.anchor.setTo(1.0, 1.0);
        this.scoreLabel.textAlign = ex.TextAlign.Right;
        this.addUIActor(this.scoreLabel);

        this.goldLabel = new ex.Label("0", this.engine.drawWidth - 20, this.engine.drawHeight - 50);
        this.goldLabel.anchor.setTo(1.0, 1.0);
        this.goldLabel.textAlign = ex.TextAlign.Right;
        this.goldLabel.scale.scaleEqual(2);
        this.addUIActor(this.goldLabel);

        this.buyButton = new ex.Actor(this.engine.drawWidth - 20, this.engine.halfDrawHeight, 64, 64, ex.Color.Red);
        this.buyButton.addDrawing("normal", new ex.Sprite(this.lib["buyButton"], 0, 0, 64, 64));
        this.buyButton.addDrawing("disabled", new ex.Sprite(this.lib["buyButtonDisabled"], 0, 0, 64, 64));
        this.buyButton.anchor.setTo(1.0, 0.5);
        this.addUIActor(this.buyButton);

        this.spawnTurretTrailer();

        while (this.needToSpawnStripeTop()) {
            this.spawnStripeTop();
        }
        while (this.needToSpawnStripeBottom()) {
            this.spawnStripeBottom();
        }
    }

    spawnStripe(y: number) {
        let stripe = new ex.Actor(0, y, 5, stripeLength, ex.Color.White);
        this.add(stripe);
        stripe.setZIndex(-100);
        this.stripes.push(stripe);
    }
    spawnStripeTop() {
        let stripeY = this.topStripePos - stripeInterval;
        this.spawnStripe(stripeY);
        this.topStripePos = stripeY;
    }

    spawnStripeBottom() {
        let stripeY = this.bottomStripePos + stripeInterval;
        this.spawnStripe(stripeY);
        this.bottomStripePos = stripeY;
    }

    needToSpawnStripeTop() {
        let cameraTop = this.camera.pos.y - this.engine.halfDrawHeight;
        let nextStripeBottom = this.topStripePos - stripeInterval + (stripeLength / 2);
        return cameraTop <= nextStripeBottom;
    }

    needToSpawnStripeBottom() {
        let cameraBottom = this.camera.pos.y + this.engine.halfDrawHeight;
        let nextStripeTop = this.bottomStripePos + stripeInterval - (stripeLength / 2);
        return cameraBottom >= nextStripeTop;
    }

    buyTurret() {
        if (this.gold < turretCost) {
            return;
        }

        this.sounds["button"].play(0.8);
        this.gold -= turretCost;
        this.spawnTurretTrailer();
    }

    update(engine: ex.Engine, delta: number): void {
        this.gameTime += delta;

        let distanceTravelled = -this.tower!.pos.y;
        let difficultyFactor = 1 + (Math.max(0, distanceTravelled) / 1000);

        if (distanceTravelled - this.lastMine > (2000 / difficultyFactor)) {
            this.spawnMine();
            this.lastMine = distanceTravelled;
        }

        if (!this.gameOver && this.gameTime - this.lastChaserTime > (5000 / this.chaserRateMultiplier)) {
            this.spawnChaser();
            this.lastChaserTime = this.gameTime;
        }

        this.buyButton!.setDrawing(this.gold >= turretCost ? "normal" : "disabled");

        let towerRacerTrait = getTrait(this.tower!, RacerTrait);

        towerRacerTrait.throttleOn = engine.input.keyboard.isHeld(ex.Input.Keys.W);
        towerRacerTrait.steerLeft = engine.input.keyboard.isHeld(ex.Input.Keys.A);
        towerRacerTrait.steerRight = engine.input.keyboard.isHeld(ex.Input.Keys.D);

        // if (!this.gameOver && towerRacerTrait.throttleOn) {
        //     let sound = this.sounds["engine"];
        //     sound.setLoop(true);
        //     if (!sound.isPlaying()) {
        //         sound.play(0.5);
        //     }
        // }
        // else {
        //     let sound = this.sounds["engine"];
        //     sound.stop();
        // }

        for (let chaser of this.chasers) {
            this.updateChaser(chaser);
        }

        super.update(engine, delta);

        if (distanceTravelled - this.lastScoreIncreaseDistance  >= 1000) {
            this.score += 100;
            this.lastScoreIncreaseDistance = distanceTravelled;
        }

        this.scoreLabel!.text = "Score: " + this.score.toString();
        this.goldLabel!.text = "Gold: " + this.gold.toString();

        while (this.needToSpawnStripeTop()) {
            this.spawnStripeTop();
        }
        while (this.needToSpawnStripeBottom()) {
            this.spawnStripeBottom();
        }
        for (let i = this.stripes.length - 1; i >= 0; --i) {
            let s = this.stripes[i];
            if (s.isOffScreen) {
                s.kill();
                this.stripes.splice(i, 1);
                this.topStripePos = findMin(this.stripes, s => s.pos.y);
                this.bottomStripePos = findMax(this.stripes, s => s.pos.y);
            }
        }
    }

    updateChaser(chaser: ex.Actor) {
        let steering = getTrait(chaser, RacerTrait);
        let toTower = this.tower!.pos.sub(chaser.pos);
        let toTowerAngle = wrap(toTower.toAngle() - chaser.rotation, -Math.PI, Math.PI);
        if (toTowerAngle < 0) {
            steering.steerLeft = true;
            steering.steerRight = false;
        }
        else {
            steering.steerLeft = false;
            steering.steerRight = true;
        }
        if (Math.abs(toTowerAngle) > Math.PI/2) {
            steering.throttleOn = false;
        }
        else {
            steering.throttleOn = true;
        }
    }

    killChaser(actor: ex.Actor) {
        this.doExplosion(actor.pos.x, actor.pos.y);
        actor.kill();
        this.sounds["boom"].play(0.5);
        this.score += 10;
        this.gold += 10;
        this.chaserRateMultiplier += 0.25
    }

    doExplosion(x: number, y: number) {
        let emitter = this.createExplosionEmitter(x, y);
        this.add(emitter);
        emitter.actions.delay(200).callMethod(() => { emitter.isEmitting = false; }).delay(5000).die();
    }

    createExplosionEmitter(x: number, y: number) {
        let emitter = new ex.ParticleEmitter(x, y, 2, 2);
        emitter.emitterType = ex.EmitterType.Circle;
        emitter.radius = 5;
        emitter.minVel = 100;
        emitter.maxVel = 200;
        emitter.minAngle = 0;
        emitter.maxAngle = 6.2;
        emitter.isEmitting = true;
        emitter.emitRate = 331;
        emitter.opacity = 0.5;
        emitter.fadeFlag = true;
        emitter.particleLife = 1000;
        emitter.maxSize = 10;
        emitter.minSize = 1;
        emitter.startSize = 0;
        emitter.endSize = 0;
        emitter.acceleration = new ex.Vector(0, 0);
        emitter.beginColor = ex.Color.Transparent;
        emitter.endColor = ex.Color.Transparent;
        return emitter;
    }

    onActorCollision(evt: ex.PreCollisionEvent, actor: ex.Actor) {
        let leftCategory = getTrait(actor, TagTrait).tag;
        let rightCategory = getTrait(evt.other, TagTrait).tag;

        switch (leftCategory)
        {
            case "mine":
                switch (rightCategory) {
                    case "tower":
                        this.destroyMine(actor);
                        this.destroyTowerTrain();
                        this.doGameOver();
                        break;
                    case "chaser":
                        this.destroyMine(actor);
                        this.killChaser(evt.other);
                        break;
                }
                break;
            case "chaser":
                switch (rightCategory) {
                    case "tower":
                    this.killChaser(actor);
                        this.destroyTowerTrain();
                        this.doGameOver();
                        break;
                }
                break;
            case "projectile": {
                switch (rightCategory) {
                    case "chaser":
                        this.killChaser(evt.other);
                        this.destroyProjectile(actor);
                        break;
                }
                break;
            }
        }
    }

    private destroyTowerTrain() {
        for (let t of this.trailers) {
            this.doExplosion(t.pos.x, t.pos.y);
            t.kill();
        }
        this.sounds["boom"].play(0.5);
    }

    private destroyProjectile(proj : ex.Actor) {
        proj.kill();
    }

    private destroyMine(mine: ex.Actor) {
        this.doExplosion(mine.pos.x, mine.pos.y);
        this.sounds["boom"].play(0.5);
        mine.kill();
    }

    doGameOver() {
        if (this.gameOver) {
            return false;
        }
        this.gameOver = true;
        setTimeout(() => { this.sounds["gameOver"].play(0.8) }, 200);
        this.addUIActor(this.gameOverText!);
        this.addUIActor(this.continueButton!);
    }

    findClosestChaser(pos: ex.Vector): ex.Actor | undefined {
        let winner;
        let winDistance = Infinity;
        for (let c of this.chasers) {
            let d = c.pos.distance(pos);
            if (d < winDistance) {
                winner = c;
                winDistance = d;
            }
        }
        return winner;
    }

    private createTower() {
        let tower = new ex.Actor(0, 0, 16, 16);
        tower.collisionType = ex.CollisionType.Passive;
        tower.rotation = -Math.PI/2;
        tower.addDrawing(this.lib["tower"]);
        tower.traits.push(new RacerTrait());
        tower.traits.push(new TagTrait("tower"));
        return tower;
    }

    private spawnChaser() {
        let chaser = this.createChaser();
        chaser.on('precollision', evt => {
            this.onActorCollision(evt!, chaser);
        });
        this.chasers.push(chaser);
        this.add(chaser);
    }

    private createChaser() {
        let chaser = new ex.Actor(ex.Util.randomIntInRange(leftRoadEdge, rightRoadEdge), this.tower!.pos.y + 300, 30, 30);
        chaser.rotation = -Math.PI/2;
        chaser.collisionType = ex.CollisionType.Active;
        chaser.addDrawing(this.lib["bombCar"]);
        let racerTrait = new RacerTrait(350);
        racerTrait.speed = 350;
        chaser.traits.push(racerTrait);
        chaser.traits.push(new TagTrait("chaser"));
        return chaser;
    }

    private spawnMine()
    {
        let mine = this.createMine();
        mine.on('precollision', evt => {
            this.onActorCollision(evt!, mine);
        });
        this.mines.push(mine);
        this.add(mine);
    }

    private createMine() {
        let mine = new ex.Actor(ex.Util.randomIntInRange(leftRoadEdge, rightRoadEdge), this.tower!.pos.y - 300, 16, 16);
        mine.collisionType = ex.CollisionType.Passive;
        mine.addDrawing(this.lib["mine"]);
        mine.traits.push(new TagTrait("mine"));
        return mine;
    }

    private spawnTurretTrailer() {
        let turret = this.createTurretTrailer();
        this.trailers.push(turret);
        this.add(turret);
    }

    private createTurretTrailer() {
        let lastTrailer = this.trailers[this.trailers.length - 1];
        let turret = new ex.Actor(lastTrailer.pos.x, lastTrailer.pos.y + 16, 16, 16, ex.Color.Red);
        turret.rotation = Math.PI/2;
        turret.collisionType = ex.CollisionType.Passive;
        turret.addDrawing(this.lib["turretTrailer"]);
        turret.traits.push(new TrailerTrait(lastTrailer));
        turret.traits.push(new AutoTurretTrait(this));
        turret.traits.push(new TagTrait("trailer"));
        return turret;
    }

    fireProjectile(pos: ex.Vector, vel: ex.Vector) {
        this.spawnProjectile(pos, vel);
        this.sounds["shoot"].play(0.5);
    }

    spawnProjectile(pos: ex.Vector, vel: ex.Vector) {
        let proj = this.createProjectile(pos, vel);
        proj.on("precollision", evt => {
            this.onActorCollision(evt!, proj);
        });
        this.add(proj);
    }

    private createProjectile(pos: ex.Vector, vel: ex.Vector) {
        let proj = new ex.Actor(0, 0, 8, 8);
        proj.collisionType = ex.CollisionType.Passive;
        proj.traits.push(new TagTrait("projectile"));
        proj.pos = pos.clone();
        proj.vel = vel.clone();
        proj.addDrawing(this.lib["bullet"]);
        return proj;
    }
}


let loader = new ex.Loader();
let textures: TextureLibrary = {
    tower: new ex.Texture("assets/tower.png"),
    mine: new ex.Texture("assets/mine.png"),
    bombCar: new ex.Texture("assets/bombcar.png"),
    turretTrailer: new ex.Texture("assets/turrettrailer.png"),
    bullet: new ex.Texture("assets/bullet.png"),
    buyButton: new ex.Texture("assets/buybutton.png"),
    buyButtonDisabled: new ex.Texture("assets/buybutton_disabled.png"),
    gameOver: new ex.Texture("assets/gameover.png"),
    title: new ex.Texture("assets/title.png"),
    continueButton: new ex.Texture("assets/continuebutton.png"),
    playButton: new ex.Texture("assets/playbutton.png"),
};

for (let loadable in textures) {
    if (textures.hasOwnProperty(loadable)) {
        loader.addResource(textures[loadable]);
    }
}

let sounds: SoundLibrary = {
    boom: new ex.Sound("sounds/boom.wav"),
    shoot: new ex.Sound("sounds/shoot.wav"),
    engine: new ex.Sound("sounds/engine.wav"),
    gameOver: new ex.Sound("sounds/gameover.wav"),
    button: new ex.Sound("sounds/button.wav"),
    title: new ex.Sound("sounds/title.wav"),
    music: new ex.Sound("sounds/music.mp3"),
}

for (let loadable in sounds) {
    if (sounds.hasOwnProperty(loadable)) {
        loader.addResource(sounds[loadable]);
    }
}

let roadColor = ex.Color.fromRGB(102, 102, 102);

let game = new ex.Engine({
    width: 640,
    height:480,
    backgroundColor: roadColor,
    canvasElementId: "gameCanvas"
});


let titleScene = new TitleScene(textures, sounds);
let gameScene = new GameScene(textures, sounds);
game.add("title", titleScene);
game.add("game", gameScene);

// Start the engine to begin the game.
game.start(loader).then(() => {
    game.goToScene("title");
    let music = sounds["music"];
    music.setLoop(true);
    music.play(0.4);
    game.input.keyboard.on("press", evt => {
        if (evt!.key == ex.Input.Keys.M) {
            if (music.isPlaying()) {
                music.stop();
            }
            else {
                music.play(0.4);
            }
        }
    });
});

